import { type ChainFullId, type ChainId, type MultichainTokenBalance } from "@umami/chains";
import { type UnifiedBalanceWithPrices } from "@umami/state";
import { getLayerZeroBridgeQuote, isSameOrWrappedToken } from "@umami/swap";

import { type EffectiveOutput, type QuoteProvider, type QuoteWithProvider } from "./types";
import { SwapValidator } from "../validation/swapValidation";

export class LayerZeroProvider implements QuoteProvider {
  canHandleSwap(
    sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    getNetworkChain: (chainId: ChainFullId) => ChainId
  ): boolean {
    // LayerZero only bridges the same token between different EVM chains, not Tezos <-> Etherlink
    if (SwapValidator.isTezosEtherlinkSwap(sellToken, buyToken, getNetworkChain)) {
      return false;
    }

    return (
      getNetworkChain(sellToken.chainId) !== getNetworkChain(buyToken.chainId) &&
      isSameOrWrappedToken(sellToken.symbol, buyToken.symbol)
    );
  }

  async fetchQuote(params: {
    sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
    sellAmount: string;
    userAddress: string;
    network: string;
    slippage: number;
    isGaslessSwap?: boolean;
  }): Promise<QuoteWithProvider | null> {
    const { sellToken, buyToken, sellAmount, network } = params;

    try {
      const layerZeroQuote = await getLayerZeroBridgeQuote({
        sellToken: {
          chainId: sellToken.chainId,
          symbol: sellToken.symbol,
          decimals: sellToken.decimals || 18,
          address: sellToken.address,
        },
        buyToken: {
          chainId: buyToken.chainId,
          symbol: buyToken.symbol,
          decimals: buyToken.decimals || 18,
          address: buyToken.address,
        },
        sellAmount,
        network: network as "mainnet" | "shadownet",
      });

      return {
        provider: "LayerZero",
        quote: layerZeroQuote,
        buyAmount: layerZeroQuote.buyAmount,
        sellAmount: layerZeroQuote.sellAmount,
      };
    } catch (error) {
      console.error("Error fetching LayerZero bridge quote:", error);
      throw new Error(
        `Failed to fetch LayerZero bridge quote: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  getValueAndUnit(
    quote: QuoteWithProvider,
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    getTokenUsdPrice: (symbol: string, amount: number) => number
  ): EffectiveOutput {
    const buyAmountNum = parseFloat(quote.buyAmount);
    const buyAmountUSD = getTokenUsdPrice(buyToken.symbol, buyAmountNum);
    const layerZeroQuote = quote.quote as any;
    const nativeFeeInEth = parseFloat(layerZeroQuote.nativeFee);
    const feeUSD = getTokenUsdPrice(layerZeroQuote.nativeFeeSymbol, nativeFeeInEth);

    if (buyAmountUSD > 0 && feeUSD > 0) {
      const netUSD = buyAmountUSD - feeUSD;

      return { value: netUSD, unit: "USD", hasUSD: true };
    }

    return { value: buyAmountNum, unit: buyToken.symbol, hasUSD: false };
  }
}
